import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ai, campaigns, type AiTone, type AiGoal, type CampaignFullResult } from "@/lib/api";
import { Sparkles, ArrowLeft, ArrowRight, Check, RefreshCw, Loader2 } from "lucide-react";

// ── Types ─────────────────────────────────────────────────────────────────────

interface Step1Form {
  productUrl: string;
  audience: string;
  goal: AiGoal;
  tone: AiTone;
  cta: string;
  ctaUrl: string;
  brandName: string;
  campaignName: string;
  autoOptimize: number;
  autoOptimizeWaitHours: number;
  autoOptimizeThreshold: number;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const GOALS: { value: AiGoal; label: string; description: string }[] = [
  { value: "announce",   label: "Announce",   description: "Launch a new product or feature" },
  { value: "promote",    label: "Promote",    description: "Drive sales or sign-ups" },
  { value: "nurture",    label: "Nurture",    description: "Build relationships over time" },
  { value: "re-engage",  label: "Re-engage",  description: "Win back inactive subscribers" },
];

const TONES: { value: AiTone; label: string }[] = [
  { value: "professional", label: "Professional" },
  { value: "casual",       label: "Casual" },
  { value: "friendly",     label: "Friendly" },
  { value: "urgent",       label: "Urgent" },
  { value: "playful",      label: "Playful" },
];

// ── Small helpers ─────────────────────────────────────────────────────────────

function domainFromUrl(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

function inputClass(hasError = false) {
  return `w-full px-4 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-coral/30 bg-paper ${
    hasError ? "border-red-400" : "border-black/[0.08]"
  }`;
}

// ── Step indicators ───────────────────────────────────────────────────────────

function StepDots({ current }: { current: 1 | 2 | 3 }) {
  return (
    <div className="flex items-center gap-2 mb-8">
      {([1, 2, 3] as const).map((n) => (
        <div key={n} className="flex items-center gap-2">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
            n < current  ? "bg-coral text-white" :
            n === current ? "bg-coral text-white ring-4 ring-coral/20" :
                            "bg-black/[0.06] text-slate"
          }`}>
            {n < current ? <Check size={12} /> : n}
          </div>
          {n < 3 && <div className={`w-12 h-0.5 rounded ${n < current ? "bg-coral" : "bg-black/[0.08]"}`} />}
        </div>
      ))}
      <span className="ml-2 text-xs text-slate font-medium">
        {current === 1 ? "Setup" : current === 2 ? "Preview & Edit" : "Save"}
      </span>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function CampaignWizardPage() {
  const navigate = useNavigate();

  const [step, setStep]       = useState<1 | 2 | 3>(1);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof Step1Form, string>>>({});

  // Bug fix: prevent concurrent AI calls if user double-clicks Generate/Regenerate
  const generateInFlight = useRef(false);

  // Step 1 form state
  const [form, setForm] = useState<Step1Form>({
    productUrl:   "",
    audience:     "",
    goal:         "promote",
    tone:         "professional",
    cta:          "",
    ctaUrl:       "",
    brandName:    "",
    campaignName: "",
    autoOptimize: 0,
    autoOptimizeWaitHours: 24,
    autoOptimizeThreshold: 20,
  });

  // Step 2 generated result + editable fields
  const [result, setResult]         = useState<CampaignFullResult | null>(null);
  const [subject, setSubject]       = useState("");
  const [preheader, setPreheader]   = useState("");
  const [htmlContent, setHtmlContent] = useState("");

  // ── Validation ──────────────────────────────────────────────────────────────

  function validateStep1(): boolean {
    const errs: Partial<Record<keyof Step1Form, string>> = {};
    if (!form.productUrl.trim()) {
      errs.productUrl = "Product URL is required";
    } else {
      try { new URL(form.productUrl); } catch { errs.productUrl = "Must be a valid URL (include https://)"; }
    }
    if (!form.audience.trim() || form.audience.trim().length < 5) {
      errs.audience = "Please describe your target audience (at least 5 characters)";
    }
    if (form.ctaUrl && form.ctaUrl.trim()) {
      try { new URL(form.ctaUrl); } catch { errs.ctaUrl = "Must be a valid URL or leave blank"; }
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  // ── Step 1 → 2: generate ───────────────────────────────────────────────────

  async function handleGenerate() {
    if (!validateStep1()) return;
    // Bug fix: block concurrent calls — disabled attr alone isn't enough
    // because React batches state updates and a double-click can slip through
    if (generateInFlight.current) return;
    generateInFlight.current = true;
    setError(null);
    setGenerating(true);
    try {
      const res = await ai.campaignFull({
        productUrl: form.productUrl.trim(),
        audience:   form.audience.trim(),
        goal:       form.goal,
        tone:       form.tone,
        cta:        form.cta.trim() || undefined,
        ctaUrl:     form.ctaUrl.trim() || undefined,
        brandName:  form.brandName.trim() || undefined,
      });
      setResult(res);
      setSubject(res.subjectLine);
      setPreheader(res.preheader);
      setHtmlContent(res.html);
      // Auto-fill campaign name from domain if blank
      if (!form.campaignName.trim()) {
        setForm((f) => ({ ...f, campaignName: domainFromUrl(form.productUrl) + " — " + f.goal }));
      }
      setStep(2);
    } catch (e: any) {
      setError(e.message || "Failed to generate campaign. Please try again.");
    } finally {
      setGenerating(false);
      generateInFlight.current = false;
    }
  }

  // Regenerate without leaving step 2
  async function handleRegenerate() {
    // Bug fix: same inFlight guard
    if (generateInFlight.current) return;
    generateInFlight.current = true;
    setError(null);
    setGenerating(true);
    try {
      const res = await ai.campaignFull({
        productUrl: form.productUrl.trim(),
        audience:   form.audience.trim(),
        goal:       form.goal,
        tone:       form.tone,
        cta:        form.cta.trim() || undefined,
        ctaUrl:     form.ctaUrl.trim() || undefined,
        brandName:  form.brandName.trim() || undefined,
      });
      setResult(res);
      setSubject(res.subjectLine);
      setPreheader(res.preheader);
      setHtmlContent(res.html);
    } catch (e: any) {
      setError(e.message || "Regeneration failed. Please try again.");
    } finally {
      setGenerating(false);
      generateInFlight.current = false;
    }
  }

  // Swap to an alternative subject line
  function applyAltSubject(text: string) {
    setSubject(text);
  }

  // ── Step 3: save as draft ──────────────────────────────────────────────────

  async function handleSave() {
    // Bug fix: validate both required fields before hitting the API
    const errs: Partial<Record<keyof Step1Form, string>> = {};
    if (!form.campaignName.trim()) {
      errs.campaignName = "Campaign name is required";
    }
    if (!subject.trim()) {
      setError("Subject line cannot be empty. Go back and edit it before saving.");
      return;
    }
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await campaigns.create({
        name:         form.campaignName.trim(),
        subject:      subject.trim(),
        preview_text: preheader,
        html_content: htmlContent,
        status:       "draft",
        auto_optimize: form.autoOptimize,
        auto_optimize_wait_hours: form.autoOptimizeWaitHours,
        auto_optimize_threshold: form.autoOptimizeThreshold,
      } as any);
      navigate("/campaigns");
    } catch (e: any) {
      setError(e.message || "Failed to save campaign. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  // ── Field helper ───────────────────────────────────────────────────────────

  function field(key: keyof Step1Form) {
    return {
      value: form[key],
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        setForm((f) => ({ ...f, [key]: e.target.value }));
        if (fieldErrors[key]) setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
      },
    };
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-paper">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-paper/90 backdrop-blur border-b border-black/[0.06] px-8 py-5 flex items-center gap-4">
        <button onClick={() => navigate("/campaigns")}
          className="p-2 rounded-lg hover:bg-black/[0.04] transition-colors text-slate">
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Sparkles size={18} className="text-coral" /> AI Campaign Wizard
          </h1>
          <p className="text-xs text-slate">Generate a complete campaign in under 2 minutes</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-10">
        <StepDots current={step} />

        {/* Global error */}
        {error && (
          <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* ── STEP 1: Inputs ── */}
        {step === 1 && (
          <div className="space-y-5">
            <div>
              <h2 className="text-lg font-bold mb-1">Tell us about your campaign</h2>
              <p className="text-sm text-slate">The AI will generate a complete subject line, email body, and HTML template.</p>
            </div>

            {/* Product URL */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1.5">
                Product / Landing Page URL <span className="text-red-400">*</span>
              </label>
              <input type="url" placeholder="https://yourproduct.com" {...field("productUrl")}
                className={inputClass(!!fieldErrors.productUrl)} />
              {fieldErrors.productUrl && <p className="mt-1 text-xs text-red-500">{fieldErrors.productUrl}</p>}
            </div>

            {/* Brand name */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1.5">Brand Name</label>
              <input type="text" placeholder="Acme Inc." {...field("brandName")}
                className={inputClass()} />
            </div>

            {/* Audience */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1.5">
                Target Audience <span className="text-red-400">*</span>
              </label>
              <textarea rows={3} placeholder="e.g. Small ecommerce store owners looking to grow their email list"
                value={form.audience}
                onChange={(e) => { setForm((f) => ({ ...f, audience: e.target.value })); if (fieldErrors.audience) setFieldErrors((p) => ({ ...p, audience: undefined })); }}
                className={inputClass(!!fieldErrors.audience)} />
              {fieldErrors.audience && <p className="mt-1 text-xs text-red-500">{fieldErrors.audience}</p>}
            </div>

            {/* Goal */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-2">Campaign Goal</label>
              <div className="grid grid-cols-2 gap-2">
                {GOALS.map((g) => (
                  <button key={g.value} type="button"
                    onClick={() => setForm((f) => ({ ...f, goal: g.value }))}
                    className={`p-3 rounded-xl border text-left transition-colors ${
                      form.goal === g.value
                        ? "border-coral bg-coral/5 ring-1 ring-coral/30"
                        : "border-black/[0.08] hover:border-black/20 bg-white"
                    }`}>
                    <p className={`text-sm font-semibold ${form.goal === g.value ? "text-coral" : "text-ink"}`}>{g.label}</p>
                    <p className="text-xs text-slate mt-0.5">{g.description}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Tone */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-2">Tone</label>
              <div className="flex flex-wrap gap-2">
                {TONES.map((t) => (
                  <button key={t.value} type="button"
                    onClick={() => setForm((f) => ({ ...f, tone: t.value }))}
                    className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                      form.tone === t.value
                        ? "bg-ink text-white border-ink"
                        : "border-black/[0.08] text-slate hover:border-black/20"
                    }`}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1.5">CTA Button Text</label>
                <input type="text" placeholder="Get Started" {...field("cta")} className={inputClass()} />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1.5">CTA URL (optional)</label>
                <input type="url" placeholder="https://yourproduct.com/signup" {...field("ctaUrl")}
                  className={inputClass(!!fieldErrors.ctaUrl)} />
                {fieldErrors.ctaUrl && <p className="mt-1 text-xs text-red-500">{fieldErrors.ctaUrl}</p>}
              </div>
            </div>

            <div className="pt-2 flex justify-end">
              <button onClick={handleGenerate} disabled={generating}
                className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-coral text-white font-semibold text-sm hover:bg-coral-dark disabled:opacity-60 shadow-md shadow-coral/20 transition-colors">
                {generating
                  ? <><Loader2 size={16} className="animate-spin" /> Generating…</>
                  : <><Sparkles size={16} /> Generate Campaign <ArrowRight size={16} /></>}
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 2: Preview & Edit ── */}
        {step === 2 && result && (
          <div className="space-y-6">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-lg font-bold mb-1">Preview & Edit</h2>
                <p className="text-sm text-slate">Edit the subject line or pick an alternative, then continue.</p>
              </div>
              <button onClick={handleRegenerate} disabled={generating}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-black/[0.08] text-xs font-semibold text-slate hover:border-black/20 transition-colors disabled:opacity-60">
                <RefreshCw size={13} className={generating ? "animate-spin" : ""} />
                {generating ? "Regenerating…" : "Regenerate"}
              </button>
            </div>

            {/* Subject line — editable */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1.5">Subject Line</label>
              <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
                className={inputClass()} maxLength={120} />
              <p className="mt-1 text-xs text-slate text-right">{subject.length}/120</p>
            </div>

            {/* Alternative subjects */}
            {result.alternativeSubjects.length > 0 && (
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate mb-2">Alternative Subject Lines</p>
                <div className="space-y-2">
                  {result.alternativeSubjects.map((alt, i) => (
                    <button key={i} type="button" onClick={() => applyAltSubject(alt.text)}
                      className={`w-full text-left p-3 rounded-xl border transition-colors ${
                        subject === alt.text
                          ? "border-coral bg-coral/5"
                          : "border-black/[0.06] bg-white hover:border-black/20"
                      }`}>
                      <p className="text-sm font-medium text-ink">{alt.text}</p>
                      <p className="text-xs text-slate mt-0.5">{alt.whyItWorks}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Preheader — editable */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1.5">Preheader Text</label>
              <input type="text" value={preheader} onChange={(e) => setPreheader(e.target.value)}
                className={inputClass()} maxLength={140} />
              <p className="mt-1 text-xs text-slate">Shown below the subject line in most inboxes. {preheader.length}/140</p>
            </div>

            {/* HTML preview */}
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate mb-2">Email Preview</p>
              <div className="rounded-xl border border-black/[0.08] overflow-hidden bg-[#F9FAFB]">
                {/* Fake browser chrome */}
                <div className="flex items-center gap-1.5 px-4 py-2 bg-white border-b border-black/[0.06]">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                  <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                  <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
                  <span className="ml-3 text-xs text-slate font-mono">Gmail</span>
                </div>
                {generating ? (
                  <div className="h-64 flex items-center justify-center">
                    <Loader2 size={24} className="animate-spin text-slate" />
                  </div>
                ) : (
                  <iframe
                    srcDoc={htmlContent}
                    className="w-full border-none"
                    style={{ height: "480px" }}
                    sandbox="allow-same-origin"
                    title="Email preview"
                  />
                )}
              </div>
            </div>

            <div className="flex justify-between pt-2">
              <button onClick={() => setStep(1)}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-black/[0.08] text-sm font-semibold text-slate hover:border-black/20 transition-colors">
                <ArrowLeft size={15} /> Back
              </button>
              <button onClick={() => setStep(3)}
                className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-coral text-white font-semibold text-sm hover:bg-coral-dark shadow-md shadow-coral/20 transition-colors">
                Continue <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 3: Save ── */}
        {step === 3 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-bold mb-1">Name & Save</h2>
              <p className="text-sm text-slate">The campaign is saved as a draft — review it in the editor before sending.</p>
            </div>

            {/* Summary card */}
            <div className="p-4 rounded-xl bg-white border border-black/[0.06] space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate font-medium">Subject</span>
                <span className="font-semibold text-ink text-right max-w-[60%] truncate">{subject}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate font-medium">Goal</span>
                <span className="font-semibold capitalize">{form.goal}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate font-medium">Tone</span>
                <span className="font-semibold capitalize">{form.tone}</span>
              </div>
            </div>

            {/* Campaign name */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1.5">
                Campaign Name <span className="text-red-400">*</span>
              </label>
              <input type="text" placeholder="e.g. Spring Launch 2026"
                value={form.campaignName}
                onChange={(e) => { setForm((f) => ({ ...f, campaignName: e.target.value })); setFieldErrors((p) => ({ ...p, campaignName: undefined })); }}
                className={inputClass(!!fieldErrors.campaignName)} />
              {fieldErrors.campaignName && <p className="mt-1 text-xs text-red-500">{fieldErrors.campaignName}</p>}
            </div>

            {/* Auto-optimize toggle */}
            <div className="p-4 rounded-xl border border-black/[0.06] bg-white space-y-3">
              <label className="flex items-center justify-between cursor-pointer">
                <div>
                  <p className="text-sm font-semibold text-ink">Auto-Optimize</p>
                  <p className="text-xs text-slate mt-0.5">
                    AI will automatically resend to non-openers with an improved subject line
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={!!form.autoOptimize}
                  onChange={(e) => setForm((f) => ({ ...f, autoOptimize: e.target.checked ? 1 : 0 }))}
                  className="w-5 h-5 rounded border-black/20 text-coral focus:ring-coral/30"
                />
              </label>
              {!!form.autoOptimize && (
                <div className="grid grid-cols-2 gap-3 pt-2 border-t border-black/[0.04]">
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-wider text-slate mb-1">Wait Time</label>
                    <select
                      value={form.autoOptimizeWaitHours || 24}
                      onChange={(e) => setForm((f) => ({ ...f, autoOptimizeWaitHours: Number(e.target.value) }))}
                      className={inputClass()}
                    >
                      <option value={6}>6 hours</option>
                      <option value={12}>12 hours</option>
                      <option value={24}>24 hours</option>
                      <option value={48}>48 hours</option>
                      <option value={72}>72 hours</option>
                    </select>
                    <p className="text-[10px] text-slate mt-0.5">Time after send to check performance</p>
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-wider text-slate mb-1">Open Rate Threshold</label>
                    <select
                      value={form.autoOptimizeThreshold || 20}
                      onChange={(e) => setForm((f) => ({ ...f, autoOptimizeThreshold: Number(e.target.value) }))}
                      className={inputClass()}
                    >
                      <option value={10}>Below 10%</option>
                      <option value={15}>Below 15%</option>
                      <option value={20}>Below 20%</option>
                      <option value={25}>Below 25%</option>
                      <option value={30}>Below 30%</option>
                    </select>
                    <p className="text-[10px] text-slate mt-0.5">Trigger if open rate is below this</p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-between pt-2">
              <button onClick={() => setStep(2)}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-black/[0.08] text-sm font-semibold text-slate hover:border-black/20 transition-colors">
                <ArrowLeft size={15} /> Back
              </button>
              <button onClick={handleSave} disabled={saving}
                className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-coral text-white font-semibold text-sm hover:bg-coral-dark disabled:opacity-60 shadow-md shadow-coral/20 transition-colors">
                {saving
                  ? <><Loader2 size={16} className="animate-spin" /> Saving…</>
                  : <><Check size={16} /> Save as Draft</>}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
